import {
  FileDirectoryIcon,
  SearchIcon,
} from "@primer/octicons-react";
import { Box } from "@primer/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import {
  FO_BAOCAO_AN_HOC_SINH_API_END_POINT,
  foDangKyAnHocSinhApi,
} from "../../../api/foDangKyAnHocSinhApi";
import ComboboxDotDangKyAn from "../../../components/combobox-dot-dang-ky-an/ComboboxDotDangKyAn";
import ComboboxHe from "../../../components/combobox-he/ComboboxHe";
import ComboboxKhoi from "../../../components/combobox-khoi/ComboboxKhoi";
import { ComboboxTruong } from "../../../components/combobox-truong";
import HocSinhShortInfo from "../../../components/hoc-sinh/HocSinhShortInfo";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import { IColumn } from "../../../components/ui/data-table/DataTable";
import Text from "../../../components/ui/text";
import MyTextInput from "../../../components/ui/text-input";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { useDangKyAnHocSinh } from "../../../hooks/useDangKyAnHocSinh";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { IFoThucDonItemRequest } from "../../../models/request/thuc-don/IFoThucDonItemRequest";

const BaoCaoNgayPage = () => {
  const dispatch = useDispatch();
  const { dm_coso_id, nam_hoc, dm_truong_id } = useCommonSelectedHook();
  const { translate, checkAccesiableTo } = useCommonContext();
  const { loadAllWithParams, status } = useDangKyAnHocSinh();

  const [foBaoCaoAns, setFoBaoCaoAns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterTruongId, setFilterTruongId] = useState<number>(0);
  const [filterKhoiId, setFilterKhoiId] = useState<number>(0);
  const [filterHeId, setFilterHeId] = useState<number>(0);
  const [filterDotDangKyId, setFilterDotDangKyId] = useState<number>(0);

  const canView = checkAccesiableTo(`${FO_BAOCAO_AN_HOC_SINH_API_END_POINT}/ngay`, "POST");

  const [dataLoad, setDataLoad] = useState<IFoThucDonItemRequest>({
    dm_coso_id,
    dm_truong_id,
    from_date: new Date().toISOString().split("T")[0],
    to_date: new Date().toISOString().split("T")[0],
  });

  const handleDateChange = (field: "from_date" | "to_date") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDataLoad((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSearchUserAsync = useCallback(async () => {
    if (!dm_coso_id || !nam_hoc || !dataLoad.from_date || !dataLoad.to_date) return;

    setLoading(true);
    try {
      const res = await foDangKyAnHocSinhApi.select_by_baocao({
        dm_coso_id,
        dm_truong_id: filterTruongId,
        fo_dotdangky_an_id: filterDotDangKyId,
        nam_hoc,
        from_date: dataLoad.from_date,
        to_date: dataLoad.to_date,
      });

      const resDiemDanh = await foDangKyAnHocSinhApi.select_by_baocao_diemdanh({
        dm_coso_id,
        dm_truong_id: filterTruongId,
        fo_dotdangky_an_id: filterDotDangKyId,
        nam_hoc,
        from_date: dataLoad.from_date,
        to_date: dataLoad.to_date,
      });

      if (res.is_success && resDiemDanh.is_success) {
        const rawData = res.data || [];
        const diemDanhData = resDiemDanh.data || [];

        const diemDanhMap: Record<number, any> = {};
        diemDanhData.forEach((item: any) => {
          diemDanhMap[item.ts_hocsinh_id] = {
            id: item.sis_diemdanh_status_id,
            name: item.sis_diemdanh_status_name,
          };
        });

        const processed = rawData.map((item: any) => ({
          ...item,
          sis_diemdanh_status_id: diemDanhMap[item.ts_hocsinh_id]?.id ?? null,
          diem_danh: diemDanhMap[item.ts_hocsinh_id]?.name || "-",
        }));

        setFoBaoCaoAns(processed);
      }
    } finally {
      setLoading(false);
    }
  }, [dm_coso_id, dm_truong_id, nam_hoc, dataLoad, filterTruongId, filterDotDangKyId]);

  const filteredData = useMemo(() => {
    return foBaoCaoAns.filter((item) => {
      return (
        (!filterDotDangKyId || item.fo_dotdangky_an_id === filterDotDangKyId) &&
        (!filterTruongId || item.dm_truong_id === filterTruongId) &&
        (!filterKhoiId || item.dm_khoi_id === filterKhoiId) &&
        (!filterHeId || item.dm_he_id === filterHeId)
      );
    });
  }, [foBaoCaoAns, filterDotDangKyId, filterTruongId, filterKhoiId, filterHeId]);

  const handleExportBaoComExcel = () => {
    if (!filteredData || !filteredData.length) {
      alert("Không có dữ liệu để xuất");
      return;
    }

    const validData = filteredData.filter((x: any) => {
      // Ưu tiên so sánh trực tiếp mã gốc
      const maBanTru = (x.ma_ban_tru || "").toUpperCase();

      return (
        ["CAS", "KAS"].includes(maBanTru) &&
        [1, 2].includes(x.sis_diemdanh_status_id)
      );
    });

    if (!validData.length) {
      alert("Không có dữ liệu thỏa mãn điều kiện để xuất");
      return;
    }

    // Danh sách tất cả tên khối có trong dữ liệu
    const allKhoiNames = Array.from(
      new Set(
        validData
          .map((x: any) => (x.ten_khoi || "").trim())
          .filter((k: string) => Boolean(k))
      )
    );

    // Xác định tên khối chuẩn cho lớp
    const getCanonicalKhoi = (tenLop: string, fallbackKhoi?: string): string => {
      const trimmedLop = (tenLop || "").trim();
      const match = trimmedLop.match(/^(\d+)/) || trimmedLop.match(/^[Ll][ớơo]p\s*(\d+)/i);
      if (match) {
        const gradeNum = match[1];
        const matched = allKhoiNames.find((k) => {
          const m = k.match(/\d+/);
          return m && m[0] === gradeNum;
        });
        if (matched) return matched;
        return `Khối ${gradeNum}`;
      }
      return (fallbackKhoi || "").trim() || "Khác";
    };

    // Gom lớp và đếm số lượng
    const classMap: Record<
      string,
      { ten_lop: string; rawKhoiCounts: Record<string, number>; so_luong: number }
    > = {};

    validData.forEach((cur: any) => {
      const tenLop = (cur.ten_lop || "").trim();
      if (!tenLop) return;

      if (!classMap[tenLop]) {
        classMap[tenLop] = {
          ten_lop: tenLop,
          rawKhoiCounts: {},
          so_luong: 0,
        };
      }
      classMap[tenLop].so_luong += 1;

      const rawKhoi = (cur.ten_khoi || "").trim();
      if (rawKhoi) {
        classMap[tenLop].rawKhoiCounts[rawKhoi] =
          (classMap[tenLop].rawKhoiCounts[rawKhoi] || 0) + 1;
      }
    });

    const summaryByClass = Object.values(classMap).map((item) => {
      let mostFrequentRawKhoi = "";
      let maxCount = 0;
      Object.entries(item.rawKhoiCounts).forEach(([khoi, count]) => {
        if (count > maxCount) {
          maxCount = count;
          mostFrequentRawKhoi = khoi;
        }
      });

      const tenKhoi = getCanonicalKhoi(item.ten_lop, mostFrequentRawKhoi);
      return {
        ten_khoi: tenKhoi,
        ten_lop: item.ten_lop,
        so_luong: item.so_luong,
      };
    });

    // Gom khối từ summaryByClass để đảm bảo tổng khối khớp hoàn toàn với tổng các lớp
    const khoiMap: Record<string, number> = {};
    summaryByClass.forEach((cls) => {
      khoiMap[cls.ten_khoi] = (khoiMap[cls.ten_khoi] || 0) + cls.so_luong;
    });

    const summaryByKhoi = Object.entries(khoiMap).map(([ten_khoi, so_luong]) => ({
      ten_khoi,
      so_luong,
    }));

    // Sort
    const sortedKhoi = [...summaryByKhoi].sort((a, b) =>
      (a.ten_khoi || "").localeCompare(b.ten_khoi || "", "vi", { numeric: true })
    );

    const sortedClass = [...summaryByClass].sort(
      (a, b) =>
        (a.ten_khoi || "").localeCompare(b.ten_khoi || "", "vi", { numeric: true }) ||
        (a.ten_lop || "").localeCompare(b.ten_lop || "", "vi", { numeric: true })
    );

    // Gom lớp theo khối để xuất dạng mỗi khối = 1 cột (Lớp, Số lượng)
    const groupedByKhoi: Record<string, any[]> = {};
    sortedClass.forEach((item) => {
      if (!groupedByKhoi[item.ten_khoi]) groupedByKhoi[item.ten_khoi] = [];
      groupedByKhoi[item.ten_khoi].push(item);
    });

    const khoiList = sortedKhoi.map((k) => k.ten_khoi);

    // Số dòng tối đa (nhiều lớp nhất)
    const maxRows = Math.max(
      0,
      ...Object.values(groupedByKhoi).map((arr) => arr.length)
    );

    const classRows: any[][] = [];
    // Các dòng dữ liệu lớp
    for (let i = 0; i < maxRows; i++) {
      const row: any[] = [];
      khoiList.forEach((khoi) => {
        const lopList = groupedByKhoi[khoi] || [];
        if (i < lopList.length) {
          row.push(lopList[i].ten_lop, lopList[i].so_luong);
        } else {
          row.push("", "");
        }
      });
      classRows.push(row);
    }

    // Tính tổng
    const totalSoLuong = sortedKhoi.reduce((sum, k) => sum + k.so_luong, 0);

    // Rows khối + dòng tổng cộng
    const khoiRows: any[][] = [
      ["Khối", "Số lượng"],
      ...sortedKhoi.map((k) => [k.ten_khoi, k.so_luong]),
      ["Tổng cộng", totalSoLuong],
    ];

    // Tạo sheet
    const dateStr = dataLoad?.from_date
      ? dataLoad.from_date.split("-").reverse().join(".")
      : "";

    const worksheet = XLSX.utils.aoa_to_sheet([
      [`BÁO CƠM BỮA TRƯA ${dateStr}`],
      [],
      ["THỐNG KÊ THEO LỚP"],
      ...classRows,
      [],
      ["THỐNG KÊ THEO KHỐI"],
      ...khoiRows,
    ]);

    // Tổng số cột = số khối * 2
    const totalCols = Math.max(khoiList.length * 2, 2);

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
    ];

    // Xuất file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BaoCom");
    XLSX.writeFile(workbook, `BaoComTrua_${dateStr}.xlsx`);
  };


  const generateColumns = useCallback(
    (): IColumn[] => [
      { caption: "Mã HS", dataField: "ma_hs", isMainColumn: true, fixed: true, width: 120 },
      {
        dataField: "ho_ten",
        width: 200,
        caption: translate("Học sinh"),
        cellRender: (data) => <HocSinhShortInfo data={data} />,
      },
      { caption: "Khối", dataField: "ten_khoi", align: "center", fixed: true, width: 50 },
      { caption: "Lớp", dataField: "ten_lop", align: "center", fixed: true, width: 50 },
      {
        caption: "Đợt đăng ký",
        dataField: "dot_dang_ky",
        align: "left",
        fixed: true,
        width: 200,
        cellRender: (data) => <TextWithEllipse text={data.dot_dang_ky} lineNumber={1} />,
      },
      {
        caption: "Đăng ký ăn",
        dataField: "ten_ban_tru",
        align: "left",
        fixed: true,
        width: 200,
        cellRender: (data) => <TextWithEllipse text={data.ten_ban_tru} lineNumber={1} />,
      },
      {
        caption: "Mã bán trú",
        dataField: "ma_ban_tru",
        align: "left",
        fixed: true,
        width: 200,
        cellRender: (data) => (
          <TextWithEllipse text={data.ma_ban_tru_theo_truong || data.ma_ban_tru} lineNumber={1} />
        ),
      },
      {
        caption: "Điểm danh",
        dataField: "diem_danh",
        align: "left",
        width: 150,
        cellRender: (data) => <TextWithEllipse text={data.diem_danh || "-"} lineNumber={1} />,
      },
    ],
    [translate]
  );

  if (!canView) {
    return (
      <Box sx={{ textAlign: "center", padding: 4 }}>
        <Text text="Base.Message.NoPermission" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 3 }}>
        <Box>
          <Text
            text="Ngày ăn"
            sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default", mr: 2 }}
          />
          <MyTextInput
            type="date"
            sx={{
              width: "180px",
              height: "32px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "border.default",
              "&:focus": {
                borderColor: "accent.emphasis",
                boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)",
              },
            }}
            value={dataLoad.from_date}
            onChange={handleDateChange("from_date")}
          />
        </Box>
        <Box>
          <ComboboxTruong
            value={filterTruongId}
            onValueChanged={(id) => {
              setFilterTruongId(id);
              setFilterKhoiId(0);
              setFilterHeId(0);
            }}
            isShowClearButton
          />
        </Box>
        <Box sx={!filterTruongId ? { pointerEvents: "none", opacity: 0.5, cursor: "not-allowed" } : {}}>
          <ComboboxKhoi
            value={filterKhoiId}
            onValueChanged={(id) => {
              setFilterKhoiId(id);
              setFilterHeId(0);
            }}
            dm_truong_id={filterTruongId || undefined}
            isShowClearButton
          />
        </Box>
        <Box sx={!filterTruongId ? { pointerEvents: "none", opacity: 0.5, cursor: "not-allowed" } : {}}>
          <ComboboxHe
            value={filterHeId}
            onValueChanged={setFilterHeId}
            dm_truong_id={filterTruongId || undefined}
            dm_khoi_id={filterKhoiId || undefined}
            isShowClearButton
          />
        </Box>
        <Box>
          <ComboboxDotDangKyAn
            value={filterDotDangKyId}
            onValueChanged={setFilterDotDangKyId}
            isShowClearButton
            isShowAll
          />
        </Box>
        <Box>
          <Button
            text="Tổng hợp"
            leadingVisual={SearchIcon}
            isLoading={loading}
            onClick={handleSearchUserAsync}
            sx={{ height: "32px" }}
          />
        </Box>
      </Box>
      <DataTable
        data={filteredData}
        isLoading={loading || status === ePageBaseStatus.is_loading}
        title="Báo cáo ăn theo ngày"
        height="calc(100vh - 240px)"
        exportEnable
        actionComponent={
          <Button
            text="Xuất Excel báo cơm"
            leadingVisual={FileDirectoryIcon}
            onClick={handleExportBaoComExcel}
          />
        }
        columns={generateColumns()}
        paging={{ enable: true, pageSizeItems: [50, 200, 500, 1000, 5000] }}
      />
    </Box>
  );
};

export default BaoCaoNgayPage;
